import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, DatePicker, Select, Space, Typography, message,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import Rest from 'tools/rest';
import Preloader from 'components/preloader';

const { RangePicker } = DatePicker;
const { Text } = Typography;

function defaultMonthRange() {
  const month = dayjs().startOf('month');
  return [month, month];
}

function monthRangeToPeriod(monthRange) {
  const [start, end] = monthRange || [];
  if (!start || !end) return [null, null];
  return [start.startOf('month'), end.endOf('month')];
}

function formatMonthPeriodLabel(from, to) {
  if (!from || !to) return '';
  const sameMonth = from.format('YYYY-MM') === to.format('YYYY-MM');
  if (sameMonth) return from.format('MMMM YYYY');
  return `${from.format('MMMM YYYY')} — ${to.format('MMMM YYYY')}`;
}

const verticalHeaderStyle = {
  writingMode: 'vertical-rl',
  transform: 'rotate(180deg)',
  whiteSpace: 'nowrap',
  maxWidth: 140,
  height: 160,
  lineHeight: 1.2,
  fontSize: 12,
  fontWeight: 600,
  color: '#434343',
  padding: '8px 4px',
  textAlign: 'left',
};

const WarehouseWriteoffReport = ({ personal = false, showUserFilter = false }) => {
  const [monthRange, setMonthRange] = useState(defaultMonthRange);

  const [userId, setUserId] = useState(undefined);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [report, setReport] = useState(null);

  const [from, to] = useMemo(() => monthRangeToPeriod(monthRange), [monthRange]);

  useEffect(() => {
    if (!showUserFilter) return;
    setUsersLoading(true);
    Rest.get('/api/v1/users/warehouse_users.json')
      .then((r) => setUsers(r.data.users || []))
      .catch(() => setUsers([]))
      .finally(() => setUsersLoading(false));
  }, [showUserFilter]);

  const loadReport = useCallback(() => {
    if (!from || !to) return;

    const params = {
      from: from.format('YYYY-MM-DD'),
      to: to.format('YYYY-MM-DD'),
      personal: personal ? '1' : '0',
    };
    if (showUserFilter && userId) params.user_id = userId;

    setLoading(true);
    Rest.get('/api/v1/material_moves/grid_report.json', { params })
      .then((r) => setReport(r.data.report || null))
      .catch((err) => {
        setReport(null);
        message.error(err.response?.data?.error || 'Не удалось построить отчёт');
      })
      .finally(() => setLoading(false));
  }, [from, to, personal, showUserFilter, userId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleMonthRangeChange = (values) => {
    if (!values?.[0] || !values?.[1]) return;
    setMonthRange([values[0].startOf('month'), values[1].startOf('month')]);
  };

  const downloadPdf = useCallback(async () => {
    if (!from || !to) return;

    const params = {
      from: from.format('YYYY-MM-DD'),
      to: to.format('YYYY-MM-DD'),
      personal: personal ? '1' : '0',
    };
    if (showUserFilter && userId) params.user_id = userId;

    setPdfLoading(true);
    try {
      const response = await Rest.get('/api/v1/material_moves/grid_report_pdf', {
        params,
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const scope = report?.user_name ? 'user' : 'gross';
      a.download = `materials_writeoff_${from.format('YYYY-MM')}_${to.format('YYYY-MM')}_${scope}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let errorMsg = 'Не удалось сформировать PDF';
      const { data } = err.response || {};
      if (data instanceof Blob && data.type?.includes('json')) {
        try {
          const text = await data.text();
          const json = JSON.parse(text);
          errorMsg = json.error || errorMsg;
        } catch (parseErr) {
          // keep default message
        }
      }
      message.error(errorMsg);
    } finally {
      setPdfLoading(false);
    }
  }, [from, to, personal, showUserFilter, userId, report?.user_name]);

  const addresses = report?.addresses || [];
  const rows = report?.rows || [];
  const addressTotals = report?.address_totals || [];

  return (
    <div>
      <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
        <Space wrap align="center">
          <Text strong>Период:</Text>
          <RangePicker
            picker="month"
            value={monthRange}
            onChange={handleMonthRangeChange}
            format="MMMM YYYY"
            allowClear={false}
          />
          {from && to && (
            <Text type="secondary">{formatMonthPeriodLabel(from, to)}</Text>
          )}
          {report?.user_name && (
            <Text type="secondary">· {report.user_name}</Text>
          )}
          {report?.scope === 'gross' && !report?.user_name && (
            <Text type="secondary">· валовый отчёт</Text>
          )}
          <Button
            icon={<DownloadOutlined />}
            loading={pdfLoading}
            disabled={!report || loading}
            onClick={downloadPdf}
          >
            Скачать PDF
          </Button>
        </Space>

        {showUserFilter && (
          <Space wrap align="center">
            <Text strong>Сотрудник:</Text>
            <Select
              allowClear
              showSearch
              placeholder="Все сотрудники (валовый отчёт)"
              style={{ minWidth: 280 }}
              loading={usersLoading}
              value={userId}
              onChange={setUserId}
              optionFilterProp="label"
              options={users.map((u) => ({ value: u.id, label: u.name }))}
            />
          </Space>
        )}
      </Space>

      <Preloader loading={loading}>
        {report && addresses.length === 0 && (
          <Text type="secondary">Нет списаний на адреса за выбранный период.</Text>
        )}

        {report && addresses.length > 0 && (
          <div style={{ overflow: 'auto', maxWidth: '100%', border: '1px solid #f0f0f0' }}>
            <table
              style={{
                borderCollapse: 'collapse',
                minWidth: '100%',
                fontSize: 13,
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      background: '#fafafa',
                      borderBottom: '1px solid #e8e8e8',
                      borderRight: '1px solid #e8e8e8',
                      padding: '8px 12px',
                      textAlign: 'left',
                      minWidth: 200,
                    }}
                  >
                    Материал
                  </th>
                  {addresses.map((addr) => (
                    <th
                      key={addr}
                      style={{
                        borderBottom: '1px solid #e8e8e8',
                        borderRight: '1px solid #f0f0f0',
                        verticalAlign: 'bottom',
                        padding: 0,
                        minWidth: 44,
                      }}
                    >
                      <div style={verticalHeaderStyle} title={addr}>
                        {addr}
                      </div>
                    </th>
                  ))}
                  <th
                    style={{
                      borderBottom: '1px solid #e8e8e8',
                      padding: '8px 10px',
                      textAlign: 'right',
                      background: '#fafafa',
                      fontWeight: 700,
                      minWidth: 72,
                      position: 'sticky',
                      right: 0,
                      zIndex: 1,
                    }}
                  >
                    Итого
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.material_id}>
                    <td
                      style={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        background: '#fff',
                        borderBottom: '1px solid #f5f5f5',
                        borderRight: '1px solid #e8e8e8',
                        padding: '8px 12px',
                        fontWeight: 500,
                      }}
                    >
                      {row.material_name}
                      {row.unit ? (
                        <Text type="secondary" style={{ marginLeft: 6, fontWeight: 400 }}>
                          ({row.unit})
                        </Text>
                      ) : null}
                    </td>
                    {row.quantities.map((qty, idx) => (
                      <td
                        key={`${row.material_id}-${idx}`}
                        style={{
                          borderBottom: '1px solid #f5f5f5',
                          borderRight: '1px solid #f5f5f5',
                          textAlign: 'center',
                          padding: '6px 4px',
                          color: qty > 0 ? '#262626' : '#d9d9d9',
                        }}
                      >
                        {qty > 0 ? qty : '—'}
                      </td>
                    ))}
                    <td
                      style={{
                        borderBottom: '1px solid #f5f5f5',
                        textAlign: 'right',
                        padding: '6px 10px',
                        fontWeight: 600,
                        background: '#fafafa',
                        position: 'sticky',
                        right: 0,
                      }}
                    >
                      {row.total}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td
                    style={{
                      position: 'sticky',
                      left: 0,
                      background: '#f5f5f5',
                      borderTop: '2px solid #e8e8e8',
                      padding: '8px 12px',
                      fontWeight: 700,
                    }}
                  >
                    Итого
                  </td>
                  {addressTotals.map((qty, idx) => (
                    <td
                      key={`total-${idx}`}
                      style={{
                        borderTop: '2px solid #e8e8e8',
                        textAlign: 'center',
                        padding: '6px 4px',
                        fontWeight: 600,
                        background: '#f5f5f5',
                      }}
                    >
                      {qty > 0 ? qty : '—'}
                    </td>
                  ))}
                  <td
                    style={{
                      borderTop: '2px solid #e8e8e8',
                      textAlign: 'right',
                      padding: '6px 10px',
                      fontWeight: 700,
                      background: '#f0f0f0',
                      position: 'sticky',
                      right: 0,
                    }}
                  >
                    {report.grand_total || 0}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Preloader>
    </div>
  );
};

export default WarehouseWriteoffReport;
